import { useEffect, useState } from "react";
import { MessagesSquare, Sparkles, Phone, Mail, RefreshCw, Flame, PhoneCall } from "lucide-react";
import api from "@/lib/api";
import { LoadingState, EmptyState } from "@/components/ui/States";
import { timeAgo } from "@/lib/leadFormat";

const PRIORITY_STYLE = {
    hot: { background: "var(--color-danger)", color: "#ffffff" },
    warm: { background: "var(--color-warning)", color: "#111111" },
    info: { background: "var(--color-line)", color: "var(--color-text)" },
};

export default function AdminChats() {
    const [sessions, setSessions] = useState(null);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [triaging, setTriaging] = useState(false);
    const [triageError, setTriageError] = useState("");
    const [alerts, setAlerts] = useState([]);
    const [analytics, setAnalytics] = useState(null);

    const loadList = () => {
        api.get("/admin/chats").then((r) => setSessions(r.data.sessions)).catch(() => setSessions([]));
        api.get("/admin/alerts", { params: { limit: 20 } }).then((r) => setAlerts(r.data.alerts)).catch(() => {});
        api.get("/admin/chat-analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    };

    const ack = async (alertId) => {
        try {
            await api.post(`/admin/alerts/${alertId}/ack`);
            setAlerts((cur) => cur.map((a) => (a.id === alertId ? { ...a, acknowledged: true } : a)));
        } catch { /* refreshed on next poll */ }
    };

    useEffect(() => { loadList(); }, []);

    const openSession = (sid) => {
        setSelected(sid);
        setDetail(null);
        setTriageError("");
        setDetailLoading(true);
        api.get(`/admin/chats/${sid}`)
            .then((r) => setDetail(r.data))
            .catch(() => setDetail(null))
            .finally(() => setDetailLoading(false));
    };

    const runTriage = async () => {
        if (!selected || triaging) return;
        setTriaging(true);
        setTriageError("");
        try {
            const r = await api.post(`/admin/chats/${selected}/triage`);
            setDetail((d) => (d ? { ...d, session: { ...d.session, triage: r.data.triage } } : d));
            loadList();
        } catch (e) {
            setTriageError(e?.response?.data?.detail || "Triage failed. Please try again.");
        } finally {
            setTriaging(false);
        }
    };

    if (sessions === null) return <LoadingState />;

    return (
        <div data-testid="admin-chats-page">
            <div className="flex items-end justify-between flex-wrap gap-2 mb-1">
                <h1 className="m-0">Visitor Chats</h1>
                <button type="button" className="a1-btn-ghost" onClick={loadList} data-testid="chats-refresh-btn">
                    <RefreshCw size={14} /> Refresh
                </button>
            </div>
            <p className="text-sm text-[var(--color-text-muted)] mb-5">
                Conversations from the website assistant. Run AI triage to get a summary, tags, and a priority so you can spot hot leads fast.
            </p>

            {analytics ? (
                <div className="grid grid-cols-3 gap-3 mb-5" data-testid="chat-analytics-strip">
                    <AnalyticsTile label="Chats · 7 days" value={analytics.web_chats_7d + analytics.voice_calls_7d} testId="ca-web-chats" />
                    <AnalyticsTile label="Chats today" value={analytics.chats_today} testId="ca-chats-today" />
                    <AnalyticsTile label="Hot leads · 7 days" value={analytics.hot_alerts_7d} hot={analytics.hot_alerts_7d > 0} testId="ca-hot-leads" />
                </div>
            ) : null}

            {alerts.filter((a) => !a.acknowledged).length > 0 ? (
                <div className="grid gap-2 mb-5" data-testid="hot-alert-log">
                    {alerts.filter((a) => !a.acknowledged).map((a) => (
                        <div key={a.id} className="alert-card" data-testid={`hot-alert-${a.id}`}>
                            <Flame size={18} className="shrink-0 mt-1" />
                            <div className="flex-1 min-w-0">
                                <p className="font-bold m-0 text-xs uppercase tracking-widest">
                                    Hot lead · {timeAgo(a.created_at)}{a.source === "auto" ? " · auto-triaged" : ""}{a.sms_status === "sent" ? " · 📱 texted John" : ""}
                                </p>
                                <p className="text-sm m-0 mt-1 leading-relaxed">{a.summary}</p>
                                {a.contact_phone ? (
                                    <p className="text-sm m-0 mt-1 font-semibold">
                                        {a.contact_name ? `${a.contact_name} — ` : ""}
                                        <a href={`tel:${a.contact_phone}`} className="underline">{a.contact_phone}</a>
                                    </p>
                                ) : null}
                            </div>
                            <div className="flex flex-col gap-2 shrink-0">
                                <button type="button" className="a1-btn-primary text-xs py-2" onClick={() => openSession(a.session_id)} data-testid={`alert-open-${a.id}`}>
                                    Open chat
                                </button>
                                <button type="button" className="a1-btn-secondary text-xs py-2" onClick={() => ack(a.id)} data-testid={`alert-ack-${a.id}`}>
                                    Acknowledge
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}

            {alerts.filter((a) => a.acknowledged).length > 0 ? (
                <details className="mb-5" data-testid="alert-history">
                    <summary className="text-sm text-[var(--color-text-muted)] cursor-pointer">
                        Alert log ({alerts.filter((a) => a.acknowledged).length} acknowledged)
                    </summary>
                    <div className="grid gap-1 mt-2">
                        {alerts.filter((a) => a.acknowledged).slice(0, 10).map((a) => (
                            <p key={a.id} className="text-xs text-[var(--color-text-muted)] m-0">
                                {timeAgo(a.created_at)} — {(a.summary || "").slice(0, 90)} · acknowledged by {a.acknowledged_by || "admin"}
                            </p>
                        ))}
                    </div>
                </details>
            ) : null}

            {sessions.length === 0 ? (
                <EmptyState headline="No visitor chats yet. When someone talks to the website assistant, the conversation shows up here." />
            ) : (
                <div className="grid lg:grid-cols-12 gap-5 items-start">
                    <div className="lg:col-span-5 grid gap-2" data-testid="chat-session-list">
                        {sessions.map((s) => {
                            const pri = s.triage?.priority;
                            return (
                                <button
                                    key={s.session_id}
                                    type="button"
                                    onClick={() => openSession(s.session_id)}
                                    className={`a1-card text-left w-full transition-colors ${selected === s.session_id ? "border-[var(--color-accent)]" : ""}`}
                                    style={{
                                        ...(selected === s.session_id ? { borderColor: "var(--color-accent)" } : null),
                                        ...(pri === "hot" ? { borderLeft: "4px solid var(--color-danger)" } : null),
                                    }}
                                    data-testid={`chat-session-row-${s.session_id}`}
                                >
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                        <span className="flex items-center gap-2">
                                            {s.channel === "voice" ? (
                                                <span className="a1-badge inline-flex items-center gap-1" style={{ background: "var(--color-accent)", color: "#081123" }} data-testid={`chat-voice-badge-${s.session_id}`}>
                                                    <PhoneCall size={10} /> CALL
                                                </span>
                                            ) : null}
                                            {pri ? (
                                                <span className="a1-badge uppercase" style={PRIORITY_STYLE[pri] || PRIORITY_STYLE.info}>{pri}</span>
                                            ) : (
                                                <span className="a1-badge" style={{ background: "var(--color-bg-card)", color: "var(--color-text-muted)", border: "1px solid var(--color-line)" }}>
                                                    not triaged
                                                </span>
                                            )}
                                            <span className="text-xs text-[var(--color-text-muted)]">{s.message_count || 0} msg</span>
                                        </span>
                                        <span className="text-xs text-[var(--color-text-muted)]">{timeAgo(s.last_message_at)}</span>
                                    </div>
                                    <p className="text-sm font-semibold m-0 line-clamp-2">{s.first_user_message || "(no message)"}</p>
                                    {s.triage?.summary ? (
                                        <p className="text-xs text-[var(--color-text-muted)] mt-1 m-0 line-clamp-2">{s.triage.summary}</p>
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>

                    <div className="lg:col-span-7">
                        {!selected ? (
                            <div className="a1-card text-center py-12 text-[var(--color-text-muted)]">
                                <MessagesSquare size={28} className="mx-auto mb-2 text-[var(--color-accent)]" />
                                Select a chat to read the transcript.
                            </div>
                        ) : detailLoading ? (
                            <LoadingState />
                        ) : !detail ? (
                            <div className="a1-card text-center py-10 text-[var(--color-text-muted)]">Couldn't load this chat.</div>
                        ) : (
                            <div className="grid gap-4" data-testid="chat-detail">
                                <div className="a1-card" data-testid="chat-triage-card">
                                    <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                                        <h3 className="text-base m-0 flex items-center gap-2">
                                            <Sparkles size={16} className="text-[var(--color-accent)]" /> AI triage
                                        </h3>
                                        <button type="button" className="a1-btn-primary" onClick={runTriage} disabled={triaging} data-testid="chat-triage-btn">
                                            {triaging ? "Triaging…" : detail.session.triage ? "Re-run triage" : "Run AI triage"}
                                        </button>
                                    </div>
                                    {triageError ? <p className="text-sm m-0 mb-2" style={{ color: "var(--color-danger)" }} data-testid="chat-triage-error">{triageError}</p> : null}
                                    {detail.session.triage ? (
                                        <TriageView triage={detail.session.triage} />
                                    ) : (
                                        <p className="text-sm text-[var(--color-text-muted)] m-0">
                                            Not triaged yet. Triage reads the whole conversation and gives you a summary, priority, tags, and any contact info shared.
                                        </p>
                                    )}
                                </div>

                                <div className="a1-card" data-testid="chat-transcript">
                                    <h3 className="text-base mt-0 mb-3">Transcript</h3>
                                    <div className="flex flex-col gap-2 max-h-[480px] overflow-y-auto pr-1">
                                        {detail.messages.map((m, i) => (
                                            <div key={i} className={`admin-chat-msg ${m.role}`}>
                                                <span className="admin-chat-msg-role">{m.role === "user" ? (detail.session.channel === "voice" ? "Caller" : "Visitor") : "Assistant"}</span>
                                                {m.content}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function AnalyticsTile({ label, value, hot, testId }) {
    return (
        <div className={`a1-card today-stat ${hot ? "border-2 border-[var(--color-danger)]" : ""}`} data-testid={testId}>
            <span className="today-stat-value">{value}</span>
            <span className="today-stat-label">{label}</span>
        </div>
    );
}

function TriageView({ triage }) {
    return (
        <div className="grid gap-3">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="a1-badge uppercase" style={PRIORITY_STYLE[triage.priority] || PRIORITY_STYLE.info}>{triage.priority}</span>
                {triage.service_tag ? <span className="a1-chip">{triage.service_tag}</span> : null}
                {triage.location_tag ? <span className="a1-chip">{triage.location_tag}</span> : null}
                {triage.sms_consent === true ? <span className="a1-chip">SMS consent ✓</span> : null}
            </div>
            <p className="text-sm leading-relaxed m-0">{triage.summary}</p>
            {(triage.contact_name || triage.contact_phone || triage.contact_email) ? (
                <div className="text-sm flex flex-col gap-1" data-testid="chat-triage-contact">
                    {triage.contact_name ? <span><strong>{triage.contact_name}</strong></span> : null}
                    {triage.contact_phone ? (
                        <a href={`tel:${triage.contact_phone}`} className="flex items-center gap-1.5"><Phone size={13} /> {triage.contact_phone}</a>
                    ) : null}
                    {triage.contact_email ? (
                        <a href={`mailto:${triage.contact_email}`} className="flex items-center gap-1.5"><Mail size={13} /> {triage.contact_email}</a>
                    ) : null}
                </div>
            ) : null}
            {triage.recommended_next_step ? (
                <p className="text-sm m-0 font-semibold" style={{ color: "var(--color-accent)" }}>
                    → {triage.recommended_next_step}
                </p>
            ) : null}
            <p className="text-xs text-[var(--color-text-muted)] m-0">
                Triaged {timeAgo(triage.triaged_at)} · AI-generated — verify contact details before acting.
            </p>
        </div>
    );
}
